import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { KeycloakService } from 'keycloak-angular';

export interface StreamEvent {
  type: 'stream_start' | 'content' | 'error' | 'done' | 'complete' | 'system_event';
  content?: string;
  stream_id?: string;
  chunk_index?: number;
  turn_path?: string;
}

export interface StreamCallbacks {
  onContent: (fullResponse: string) => void;
  onError: (error: string) => void;
  onComplete: (fullResponse: string) => void;
  onStart?: (streamId: string, turnPath?: string) => void;
  onSystemEvent?: (event: string) => void;
}

export interface StreamState {
  has_active_stream: boolean;
  stream_id?: string;
  chunk_count?: number;
  state?: string;
  completed_at?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MuxStreamService {
  private currentStreamId: string | null = null;
  private abortController: AbortController | null = null;
  public isGenerating: boolean = false;

  constructor(private keycloak: KeycloakService) {}

  /**
   * Get auth token, or empty string in local mode
   */
  private async getAuthToken(): Promise<string> {
    if (ConfigService.localMode) {
      return '';
    }
    try {
      await this.keycloak.updateToken(30);
      return await this.keycloak.getToken() || '';
    } catch (e) {
      console.error('Failed to get auth token:', e);
      return '';
    }
  }

  /**
   * Send a streaming message to an agent
   */
  async sendStreamingMessage(
    agentId: string,
    sessionId: string,
    content: string,
    callbacks: StreamCallbacks
  ): Promise<void> {
    this.isGenerating = true;
    this.abortController = new AbortController();
    let fullResponse = '';
    let completeCalled = false;

    // Timing diagnostics
    const timings = {
      start: performance.now(),
      tokenFetched: 0,
      firstByte: 0,
      firstContent: 0,
      chunkCount: 0,
      lastChunk: 0
    };

    try {
      const token = await this.getAuthToken();
      timings.tokenFetched = performance.now();

      const response = await fetch(`${ConfigService.muxUrl}/agents/${agentId}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content,
          session_id: sessionId,
          use_tools: true
        }),
        signal: this.abortController.signal
      });

      timings.firstByte = performance.now();
      console.log(`⏱️ [Stream] First byte (response headers): ${(timings.firstByte - timings.start).toFixed(0)}ms`);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonData: StreamEvent = JSON.parse(line.slice(6));

              if (jsonData.type === 'stream_start') {
                if (jsonData.stream_id) {
                  this.currentStreamId = jsonData.stream_id;
                  callbacks.onStart?.(jsonData.stream_id, jsonData.turn_path);
                }
              } else if (jsonData.type === 'content') {
                timings.chunkCount++;
                timings.lastChunk = performance.now();
                if (timings.firstContent === 0) {
                  timings.firstContent = performance.now();
                  console.log(`⏱️ [Stream] First content chunk: ${(timings.firstContent - timings.start).toFixed(0)}ms`);
                }
                fullResponse += jsonData.content || '';
                callbacks.onContent(fullResponse);
              } else if (jsonData.type === 'system_event') {
                let eventName = jsonData.content || '';
                try { eventName = JSON.parse(eventName).event || eventName; } catch {}
                callbacks.onSystemEvent?.(eventName);
              } else if (jsonData.type === 'error') {
                callbacks.onError(jsonData.content || 'Unknown error');
              } else if (jsonData.type === 'done' || jsonData.type === 'complete') {
                const totalTime = performance.now() - timings.start;
                console.log(`⏱️ [Stream] Complete! Total: ${totalTime.toFixed(0)}ms, Chunks: ${timings.chunkCount}, Response length: ${fullResponse.length} chars`);
                if (!completeCalled) {
                  completeCalled = true;
                  callbacks.onComplete(fullResponse);
                }
                break;
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }

      // If we exit the loop without a 'done' event, still call complete
      if (fullResponse && !completeCalled) {
        completeCalled = true;
        const totalTime = performance.now() - timings.start;
        console.log(`⏱️ [Stream] Exit loop (no done event). Total: ${totalTime.toFixed(0)}ms, Chunks: ${timings.chunkCount}`);
        callbacks.onComplete(fullResponse);
      }

    } catch (error: any) {
      const totalTime = performance.now() - timings.start;
      console.log(`⏱️ [Stream] Error after ${totalTime.toFixed(0)}ms:`, error.message);
      if (error.name === 'AbortError') {
        // Stream was cancelled
        if (!completeCalled) {
          completeCalled = true;
          callbacks.onComplete(fullResponse);
        }
      } else {
        callbacks.onError(error.message || 'Stream failed');
      }
    } finally {
      this.isGenerating = false;
      this.abortController = null;
    }
  }

  /**
   * Cancel the current stream
   */
  async cancelGeneration(): Promise<void> {
    if (this.abortController) {
      this.abortController.abort();
    }

    if (this.currentStreamId) {
      try {
        const token = await this.getAuthToken();
        await fetch(`${ConfigService.muxUrl}/streams/${this.currentStreamId}/cancel`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Error cancelling stream on server:', error);
      }
    }

    this.isGenerating = false;
    this.currentStreamId = null;
  }

  /**
   * Check if there's an active stream for the session and reconnect if needed.
   * Returns 'reconnected' if connected to active stream, 'recently_completed'
   * if the stream just finished (caller should re-fetch messages), or false.
   */
  async checkAndReconnectStream(
    sessionId: string,
    callbacks: StreamCallbacks
  ): Promise<boolean | 'recently_completed'> {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(
        `${ConfigService.muxUrl}/sessions/${sessionId}/stream/state`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        console.log('No stream state available for session');
        return false;
      }

      const streamState: StreamState = await response.json();

      if (streamState.has_active_stream && streamState.stream_id) {
        console.log(`🔄 Found active stream ${streamState.stream_id} with ${streamState.chunk_count} chunks`);
        await this.reconnectToStream(streamState.stream_id, 0, callbacks);
        return true;
      }

      // Check for recently completed stream — covers the race where the stream
      // completed between loadSession() and this state check. Return the stream
      // state so the caller can re-fetch messages from disk if needed.
      if (!streamState.has_active_stream && streamState.stream_id && streamState.completed_at) {
        const completedAgo = Date.now() - new Date(streamState.completed_at).getTime();
        if (completedAgo < 30000) { // Completed within last 30 seconds
          console.log(`⚡ Recently completed stream ${streamState.stream_id} (${(completedAgo / 1000).toFixed(1)}s ago)`);
          return 'recently_completed';
        }
      }

      return false;
    } catch (error) {
      console.warn('Could not check stream state:', error);
      return false;
    }
  }

  /**
   * Reconnect to an active or recently completed stream
   */
  private async reconnectToStream(
    streamId: string,
    fromChunk: number,
    callbacks: StreamCallbacks
  ): Promise<void> {
    console.log(`🔄 Reconnecting to stream ${streamId} from chunk ${fromChunk}`);

    this.isGenerating = true;
    this.currentStreamId = streamId;
    this.abortController = new AbortController();
    let fullResponse = '';

    try {
      const token = await this.getAuthToken();
      const response = await fetch(
        `${ConfigService.muxUrl}/streams/${streamId}/reconnect?from_chunk=${fromChunk}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: this.abortController.signal
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to reconnect: ${response.status}`);
      }

      callbacks.onStart?.(streamId);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonData = JSON.parse(line.slice(6));

              if (jsonData.type === 'reconnect_start') {
                console.log(`📡 Reconnect started: ${jsonData.total_chunks} chunks available`);
              } else if (jsonData.type === 'replay_complete') {
                console.log(`✅ Replay complete, caught up to chunk ${jsonData.caught_up_to}`);
              } else if (jsonData.type === 'content') {
                fullResponse += jsonData.content || '';
                callbacks.onContent(fullResponse);
              } else if (jsonData.type === 'system_event') {
                let eventName = jsonData.content || '';
                try { eventName = JSON.parse(eventName).event || eventName; } catch {}
                callbacks.onSystemEvent?.(eventName);
              } else if (jsonData.type === 'error') {
                callbacks.onError(jsonData.content || 'Unknown error');
              } else if (jsonData.type === 'done' || jsonData.type === 'complete') {
                console.log('✅ Stream complete');
                callbacks.onComplete(fullResponse);
                break;
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', line, e);
            }
          }
        }
      }

      // If we exit the loop without a 'done' event, still call complete
      if (fullResponse) {
        callbacks.onComplete(fullResponse);
      }

    } catch (error: any) {
      console.error('Reconnection failed:', error);
      if (error.name !== 'AbortError') {
        callbacks.onError(`Reconnection failed: ${error.message}`);
      }
    } finally {
      this.isGenerating = false;
      this.abortController = null;
    }
  }
}
